import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Monitor,
  CreditCard,
  Joystick,
  Users,
  Brain,
  Boxes,
  Settings,
  Network,
  LogOut,
  Wifi,
  WifiOff,
  PackagePlus,
  Download,
  PanelLeftClose,
  PanelLeft,
  LayoutDashboard,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useDashboardList } from '../dashboards/useDashboards';
import type { DashboardSummary } from '../dashboards/types';
import type { OrgType } from '../types';

// ── Nav structure per org type ──────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

function getNavSections(orgType: OrgType, features: string[]): NavSection[] {
  const sections: NavSection[] = [];

  // Manufacturer section (Kinetica only)
  if (orgType === 'kinetica') {
    sections.push({
      label: 'Manufacturer',
      items: [
        { to: '/fleet', label: 'Global Fleet', icon: <Monitor size={18} /> },
        { to: '/dashboards', label: 'Dashboards', icon: <LayoutDashboard size={18} /> },
        { to: '/integrators', label: 'Integrator Network', icon: <Network size={18} /> },
        { to: '/commissioning', label: 'Commissioning', icon: <PackagePlus size={18} /> },
        { to: '/ota', label: 'OTA Updates', icon: <Download size={18} /> },
        { to: '/skus', label: 'SKU Library', icon: <Boxes size={18} /> },
        { to: '/models', label: 'Model Training', icon: <Brain size={18} /> },
        { to: '/remote', label: 'Remote Ops', icon: <Joystick size={18} /> },
      ],
    });
  }

  // Integrator section
  if (orgType === 'integrator') {
    sections.push({
      label: 'Integrator',
      items: [
        { to: '/fleet', label: 'Client Fleet', icon: <Monitor size={18} /> },
        { to: '/dashboards', label: 'Dashboards', icon: <LayoutDashboard size={18} /> },
        { to: '/clients', label: 'My Clients', icon: <Network size={18} /> },
        { to: '/commissioning', label: 'Commissioning', icon: <PackagePlus size={18} /> },
        { to: '/ota', label: 'OTA Updates', icon: <Download size={18} /> },
        { to: '/skus', label: 'SKU Library', icon: <Boxes size={18} /> },
        { to: '/models', label: 'Model Training', icon: <Brain size={18} /> },
        { to: '/remote', label: 'Remote Ops', icon: <Joystick size={18} /> },
      ],
    });
  }

  // Client section
  if (orgType === 'client') {
    const items: NavItem[] = [
      { to: '/fleet', label: 'My Fleet', icon: <Monitor size={18} /> },
      { to: '/dashboards', label: 'Dashboards', icon: <LayoutDashboard size={18} /> },
    ];
    if (features.includes('remote_access')) {
      items.push({ to: '/remote', label: 'Remote Ops', icon: <Joystick size={18} /> });
    }
    sections.push({ label: 'Operations', items });
  }

  // Common section
  sections.push({
    label: 'Account',
    items: [
      { to: '/billing', label: 'Billing', icon: <CreditCard size={18} /> },
      { to: '/users', label: 'Users', icon: <Users size={18} /> },
      { to: '/settings', label: 'Settings', icon: <Settings size={18} /> },
    ],
  });

  return sections;
}

// ── Component ───────────────────────────────────────────────────────

interface SidebarProps {
  mqttConnected: boolean;
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ mqttConnected, collapsed, onToggle }: SidebarProps) {
  const { user, logout } = useAuth();
  // Hooks must run unconditionally, before any early return.
  const [dashOpen, setDashOpen] = useState(true);
  const dashboardListQuery = useDashboardList();
  if (!user) return null;

  const sections = getNavSections(user.org_type, user.features);

  const dashboards = dashboardListQuery.data?.dashboards ?? [];
  const isMine = (d: DashboardSummary) => d.scope === 'user' && d.created_by === user.sub;
  const myDashboards = dashboards.filter(isMine);
  const orgDashboards = dashboards.filter((d) => !isMine(d));

  const orgColors: Record<OrgType, string> = {
    kinetica: 'bg-orange-500/15 text-orange-400',
    integrator: 'bg-blue-500/15 text-blue-400',
    client: 'bg-emerald-500/15 text-emerald-400',
  };

  return (
    <aside className={`fixed left-0 top-0 bottom-0 bg-surface-1 border-r border-surface-4 flex flex-col z-40 transition-[width] duration-200 ${collapsed ? 'w-14' : 'w-60'}`}>
      {/* Logo */}
      <div className={`flex items-center border-b border-surface-3 ${collapsed ? 'justify-center px-0 py-4' : 'px-5 py-4'}`}>
        {collapsed ? (
          <button
            onClick={onToggle}
            className="text-gray-500 hover:text-gray-300 transition-colors"
            aria-label="Expand sidebar"
          >
            <PanelLeft size={18} />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-8 h-8 bg-kinetica-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0">
                K
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">Kinetica</div>
                <div className="text-[10px] text-gray-500">portal.kinetica.systems</div>
              </div>
            </div>
            <button
              onClick={onToggle}
              className="text-gray-500 hover:text-gray-300 transition-colors shrink-0"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </>
        )}
      </div>

      {/* Org badge */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-surface-3">
          <div className={`badge ${orgColors[user.org_type]} text-[10px]`}>
            {user.org_type.toUpperCase()}
          </div>
          <div className="text-xs text-gray-300 mt-1 font-medium truncate">{user.org_name}</div>
          <div className="text-[10px] text-gray-500 mt-0.5 capitalize">{user.tier} tier</div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section) => (
          <div key={section.label} className="mb-1">
            {!collapsed && (
              <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                {section.label}
              </div>
            )}
            {section.items.map((item) =>
              item.to === '/dashboards' ? (
                <DashboardsNav
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  open={dashOpen}
                  onToggle={() => setDashOpen((v) => !v)}
                  myDashboards={myDashboards}
                  orgDashboards={orgDashboards}
                />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2 rounded-lg text-sm transition-colors ${collapsed ? 'justify-center mx-2 px-0' : 'px-4 mx-2'} ${
                      isActive
                        ? 'bg-kinetica-600/15 text-kinetica-400 font-medium'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-surface-2'
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  {item.icon}
                  {!collapsed && item.label}
                </NavLink>
              ),
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={`border-t border-surface-3 space-y-2 ${collapsed ? 'px-1 py-3' : 'px-4 py-3'}`}>
        <div className={`flex items-center gap-2 text-[10px] ${collapsed ? 'justify-center' : ''}`}>
          {mqttConnected ? (
            <Wifi size={12} className="text-emerald-400" />
          ) : (
            <WifiOff size={12} className="text-gray-500" />
          )}
          {!collapsed && (
            <span className={mqttConnected ? 'text-emerald-400' : 'text-gray-500'}>
              {mqttConnected ? 'Live' : 'Disconnected'}
            </span>
          )}
        </div>
        {!collapsed && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400 truncate">{user.email}</div>
            <button onClick={logout} className="text-gray-500 hover:text-gray-300 transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center">
            <button onClick={logout} className="text-gray-500 hover:text-gray-300 transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

// ── Expandable Dashboards nav entry ─────────────────────────────────

interface DashboardsNavProps {
  item: NavItem;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  myDashboards: DashboardSummary[];
  orgDashboards: DashboardSummary[];
}

function DashboardsNav({ item, collapsed, open, onToggle, myDashboards, orgDashboards }: DashboardsNavProps) {
  // Collapsed sidebar: render the icon-only NavLink exactly like other items.
  if (collapsed) {
    return (
      <NavLink
        to={item.to}
        end
        className={({ isActive }) =>
          `flex items-center gap-3 py-2 rounded-lg text-sm transition-colors justify-center mx-2 px-0 ${
            isActive
              ? 'bg-kinetica-600/15 text-kinetica-400 font-medium'
              : 'text-gray-400 hover:text-gray-200 hover:bg-surface-2'
          }`
        }
        title={item.label}
      >
        {item.icon}
      </NavLink>
    );
  }

  const hasAny = myDashboards.length > 0 || orgDashboards.length > 0;

  return (
    <div>
      {/* Top row: NavLink to the builder + a chevron that toggles the sub-list.
          `end` keeps this row active only on exactly /dashboards (not /dashboards/:id). */}
      <div className="flex items-center mx-2">
        <NavLink
          to={item.to}
          end
          className={({ isActive }) =>
            `flex flex-1 items-center gap-3 py-2 pl-4 rounded-lg text-sm transition-colors ${
              isActive
                ? 'bg-kinetica-600/15 text-kinetica-400 font-medium'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-2'
            }`
          }
        >
          {item.icon}
          {item.label}
        </NavLink>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Toggle dashboards list"
          aria-expanded={open}
          className="p-1 mr-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {open && (
        <div className="mt-0.5 mb-1">
          {!hasAny && (
            <div className="px-4 ml-6 py-1 text-xs text-gray-600">No dashboards yet</div>
          )}
          {myDashboards.length > 0 && (
            <DashboardGroup label="My" dashboards={myDashboards} />
          )}
          {orgDashboards.length > 0 && (
            <DashboardGroup label="Org" dashboards={orgDashboards} />
          )}
        </div>
      )}
    </div>
  );
}

function DashboardGroup({ label, dashboards }: { label: string; dashboards: DashboardSummary[] }) {
  return (
    <div className="mb-1">
      <div className="px-4 ml-6 py-1 text-[9px] font-semibold text-gray-600 uppercase tracking-wider">
        {label}
      </div>
      {dashboards.map((d) => (
        <NavLink
          key={d.id}
          to={`/dashboards/${d.id}`}
          className={({ isActive }) =>
            `block ml-6 mr-2 pl-4 pr-2 py-1.5 rounded-lg text-[13px] truncate transition-colors ${
              isActive
                ? 'bg-kinetica-600/15 text-kinetica-400 font-medium'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-2'
            }`
          }
          title={d.name}
        >
          {d.name}
        </NavLink>
      ))}
    </div>
  );
}
